import React, { useState, useEffect } from "react";
import { Routes, Route } from "react-router-dom";
import { AppContext } from "../utilities/context";
import { CartProvider } from "../context/CartContext";
import Home from "./Home";
import Account from "./Account";
// import Shows from "./Shows";
// import DynamicPage from "./DynamicPage";
// import SandBox from "./SandBox";
// import SandBoxBee from "./SandBoxBee";
import NoMatch from "./NoMatch";
import { IntlProvider } from "react-intl";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import axios from "axios";
import Login from "./Login";
import SignupForm from "./SignupForm";
import Terms from "./Terms";
import Privacy from "./Privacy";
import Help from "./Help";
import CustomerHelp from "./CustomerHelp";
import Products from "./Products";
import Orders from "./Orders";
import Locations from "./Locations";
import Pricing from "./Pricing";
import Preferences from "./Preferences";
import Shop from "./Shop";
import Categories from "./Categories";
import Tags from "./Tags";
import LocationEndPoint from "./LocationEndPoint";
import ForgotPassword from "./ForgotPassword";
import ResetPassword from "./ResetPassword";
import PrivateRoute from "./PrivateRoute";
import OrderConfirmation from "./OrderConfirmation";
import OrdersByStatus from "./OrdersByStatus";
import LocationsPosition from "./LocationsPosition";
import "@fontsource/roboto/300.css";
import "@fontsource/roboto/400.css";
import "@fontsource/roboto/500.css";
import "@fontsource/roboto/700.css";

axios.defaults.withCredentials = true;

const App = () => {
  const languageMap = {
    es: "es",
    "es-ar": "es",
    "es-mx": "es",
    pt: "pt",
    "pt-br": "pt",
    "pt-pt": "pt",
  };

  const locale = navigator.language.toLowerCase();
  const langCode = locale.split("-")[0]; // obtiene 'pt' de 'pt-br'
  const lang = languageMap[locale] || languageMap[langCode] || "en";

  const [tenantConfig, setTenantConfig] = useState(null);
  const [user, setUser] = useState(null);
  const [breadcrumbs, setBreadcrumbs] = useState(null);
  const [loading, setLoading] = useState(true); // Añadir estado de carga
  const [alert, setAlert] = useState(null);
  const [localeMessages, setLocaleMessages] = useState(null);

  const updateTenantState = (payload) => {
    setTenantConfig(payload);
  };
  const updateUserState = (payload) => {
    setUser(payload);
  };
  const updateBreadcrumbsState = (object) => {
    setBreadcrumbs(object);
  };
  const updateAlertState = (object) => {
    setAlert(object);
  };

  // useEffect hook with an empty dependency array means this effect will only run once, like componentDidMount
  useEffect(() => {
    setLoading(true);
    getTenant();
    getUser();
  }, []);

  useEffect(() => {
    async function loadMessages() {
      let locale = navigator.language.toLowerCase();
      let langCode = locale.split("-")[0];
      let tenant = tenantConfig?.appId;
      let mod;
      try {
        if (tenant === "qrflow" && langCode === "es") {
          mod = await import("../lang/qrflow/es.json");
        } else if (tenant === "qrflow" && langCode === "en") {
          mod = await import("../lang/qrflow/en.json");
        } else if (tenant === "qrflow" && langCode === "pt") {
          mod = await import("../lang/qrflow/pt.json");
        } else if (tenant === "replybot" && langCode === "es") {
          mod = await import("../lang/replybot/es.json");
        } else if (tenant === "replybot" && langCode === "en") {
          mod = await import("../lang/replybot/en.json");
        } else if (tenant === "replybot" && langCode === "pt") {
          mod = await import("../lang/replybot/pt.json");
        } else {
          mod = { default: {} };
        }
        setLocaleMessages(mod.default);
      } catch (e) {
        setLocaleMessages({});
      }
    }
    if (tenantConfig) {
      loadMessages();
    }
  }, [tenantConfig]);

  function getTenant() {
    if (!tenantConfig) {
      axios
        .get("/api/tenant/config")
        .then((response) => {
          if (response.data) {
            updateTenantState(response.data);
            setLoading(false);
          }
        })
        .catch((error) => {
          updateTenantState(null);
          setLoading(false);
        });
    }
    return tenantConfig;
  }

  function getUser() {
    if (!user) {
      axios
        .get("/api/test/user/")
        .then((response) => {
          if (response.data.id) {
            updateUserState(response.data);
            setLoading(false);
          }
        })
        .catch((error) => {
          updateUserState(null);
          setLoading(false);
        });
    }
    return user;
  }

  function GameWrapper() {
    window.location.replace("/game");
    return null;
  }

  if (loading || !localeMessages) {
    return <div>Cargando...</div>; // Muestra un loader mientras carga o si no hay traducciones
  }

  return (
    <AppContext.Provider
      value={{
        user,
        updateUserState,
        updateBreadcrumbsState,
        breadcrumbs,
        alert,
        updateAlertState,
        tenantConfig,
        updateTenantState,
      }}
    >
      <CartProvider>
        <IntlProvider locale={locale} messages={localeMessages}>
          <LocalizationProvider dateAdapter={AdapterMoment}>
            <Routes>
              <Route exact path="/" element={<Home />} />
              <Route exact path="/terms" element={<Terms />} />
              <Route exact path="/privacy" element={<Privacy />} />
              <Route exact path="/help" element={<Help />} />
              <Route exact path="/help-customer" element={<CustomerHelp />} />
              <Route exact path="/pricing" element={<Pricing />} />
              <Route exact path="/login" element={<Login />} />
              <Route
                exact
                path="/forgot-password"
                element={<ForgotPassword />}
              />
              <Route
                path="/reset-password/:token"
                element={<ResetPassword />}
              />
              <Route exact path="/signup" element={<SignupForm />} />
              <Route
                exact
                path="/account"
                element={
                  <PrivateRoute user={user}>
                    <Account />
                  </PrivateRoute>
                }
              />
              <Route
                exact
                path="/products"
                element={
                  <PrivateRoute user={user}>
                    <Products />
                  </PrivateRoute>
                }
              />
              <Route
                exact
                path="/categories"
                element={
                  <PrivateRoute user={user}>
                    <Categories />
                  </PrivateRoute>
                }
              />
              <Route
                exact
                path="/tags"
                element={
                  <PrivateRoute user={user}>
                    <Tags />
                  </PrivateRoute>
                }
              />
              <Route
                exact
                path="/preferences"
                element={
                  <PrivateRoute user={user}>
                    <Preferences />
                  </PrivateRoute>
                }
              />
              <Route
                path="/shop"
                element={
                  <PrivateRoute user={user}>
                    <Shop />
                  </PrivateRoute>
                }
              />
              <Route
                path="/locations-position"
                element={
                  <PrivateRoute user={user}>
                    <LocationsPosition />
                  </PrivateRoute>
                }
              />
              <Route
                exact
                path="/orders"
                element={
                  <PrivateRoute user={user}>
                    <Orders />
                  </PrivateRoute>
                }
              />
              <Route
                exact
                path="/locations"
                element={
                  <PrivateRoute user={user}>
                    <Locations />
                  </PrivateRoute>
                }
              />
              <Route
                exact
                path="/orders-by-status"
                element={
                  <PrivateRoute user={user}>
                    <OrdersByStatus />
                  </PrivateRoute>
                }
              />
              <Route path="/qr/:hashId" element={<LocationEndPoint />} />
              <Route
                path="/order-confirmation/:orderId"
                element={<OrderConfirmation />}
              />
              <Route element={<NoMatch />} />
            </Routes>
          </LocalizationProvider>
        </IntlProvider>
      </CartProvider>
    </AppContext.Provider>
  );
};

export default App;
